import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { usePublishedLanding } from '@/entities/site-content';
import { DisplayPreferences } from '@/features/switch-display-preferences';
import { Toolbar } from '@/shared/ui';

interface AppHeaderProps {
  /** True on a screen that uses the wider two-column measure below the bar. */
  readonly wide?: boolean;
}

/**
 * Whose app this is, and the two controls that decide how it reads.
 *
 * The clinic's own booking logo leads the bar once it is published from the
 * panel's «لوگو»; until then, or if the site content cannot be read, the plain
 * brand square stands in — the header never waits on it.
 *
 * Language and theme are on the first screen, not only behind the account tab:
 * the account tab asks for a sign-in, and someone who landed in a language they
 * cannot read has no way to find it. The account tab keeps its copy too.
 */
export function AppHeader({ wide = false }: AppHeaderProps): JSX.Element {
  const { t } = useTranslation('common');
  const landing = usePublishedLanding();
  const logo = landing.data?.isPublished === true ? landing.data.bookingLogo : null;

  return (
    <Toolbar wide={wide}>
      <div className="flex min-w-0 items-center gap-2.5">
        {logo === null ? (
          <span
            aria-hidden="true"
            className="size-8 shrink-0 rounded-[var(--radius-sm)] bg-primary shadow-sm"
          />
        ) : (
          <img
            alt=""
            className="h-9 w-auto max-w-28 shrink-0 object-contain"
            decoding="async"
            height={logo.height}
            src={logo.url}
            width={logo.width}
          />
        )}
        <span className="truncate type-headline vibrant">{t('appName')}</span>
      </div>
      <DisplayPreferences />
    </Toolbar>
  );
}
